// ============================================================================
// STATISTIQUES › CLIENTS — un univers à quatre onglets (Résumé · Fidélité · Campagnes · Reporting clients). Réf. maquette du
// back-office (« UNIVERS STATISTIQUES · CLIENTS ») et la décision S4 de son bilan.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : encartement, clients actifs et nouveaux, réachat, paniers, joignables,
//    segments et cohortes viennent du serveur (`sales/stats-clients.ts`). Ce qui se calcule ici est de la géométrie.
// ⚠️ LES SEGMENTS SONT CEUX DU CŒUR (RFM sur deux ans), jamais les six de la maquette ; sous la population minimale, le
//    motif du refus s'affiche à leur place.
// ⚠️ LES FILTRES NE RESTREIGNENT QUE LES VENTES DE LA PÉRIODE : joignables, segments et cohortes portent sur tout le
//    fichier clients, et chaque carte concernée le dit.
// ⚠️ LA FIDÉLITÉ COMPTE LA CAGNOTTE PAR LE CŒUR (`sales/stats-clients-loyalty.ts`) ; elle ne se filtre pas par article, et sa
//    barre ne l'offre pas. L'encours, les paliers et les titres sont à date, sans comparaison.
// ⚠️ LES CAMPAGNES DISENT CE QUI EST REVENU ET, À PART, L'EFFET FACE AU TÉMOIN (`sales/stats-clients-campaigns.ts`) — ni
//    délivrés, ni STOP, ni « CA rapporté » (S4).
// ⚠️ LE REPORTING CLIENTS REGROUPE, IL NE REDÉFINIT RIEN : chaque client y est lu comme sa fiche (`sales/stats-clients-report.ts`),
//    sans période ni filtre, et les segments sont ceux du cœur.
// ============================================================================
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, EmptyState, Icon, showToast, Tabs, Trans, useChartTip } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { toInputValue, currentCurrency } from '@orgazone/money-format';
import { api, type ClientsCampaignsView, type ClientsLoyaltyView, type ClientsReportView, type ClientsSummaryView } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { useStoreScope } from '../store-context';
import { useAncreDuJour } from '../periode';
import { exporterCsv } from './export-csv';
import { StatsBar, defaultSettings, isoOf, rangeOf, type StatsSettings } from './stats-bar';
import { StatsColumnsButton, useStatsColumns } from './stats-grid';
import { csvRate, dTx, eur0, fullMoney, pc } from './stats-format';
import { typeUi } from './clients-kit';
import { phraseEcart, signedPoints } from './campaign-effect';
import { etatDe as campaignState } from './marketing';
import { channelLabel } from '../campaign-channels';
import { CARD, CARD_ROW, CardHead, EMPTY, KPI_ROW, KPI_TILE, KpiCard, KpiLabel, MONO_LABEL, TONES, bucketLabels, type Tone } from './stats-resume';
import { Avatar, toneOf } from './stats-vendeurs';
import { ReadFailed, asOfNote } from './stats-ventes';
import { RFM_SEGMENT_TONES, rfmSegmentHint, rfmSegmentLabel } from '../rfm-segments';

type TabId = 'resume' | 'fidelite' | 'campagnes' | 'reporting';
const TABS: { id: TabId; label: Translatable; icon: IconName }[] = [
  { id: 'resume', label: key('Résumé'), icon: 'dashboard' },
  { id: 'fidelite', label: key('Fidélité'), icon: 'star' },
  { id: 'campagnes', label: key('Campagnes'), icon: 'send' },
  { id: 'reporting', label: key('Reporting clients'), icon: 'table' },
];

type Phase = { phase: 'idle' } | { phase: 'loading' } | { phase: 'ready' } | { phase: 'failed'; message: string };

/** `AAAA-MM-JJ` → `AAAAMMJJ`, la forme que le serveur lit. */
const ymd = (iso: string): string => iso.replace(/-/g, '');

/** La part d'une barre, en largeur CSS — un taux `null` ne dessine rien. */
const width = (bp: number | null): string => `${Math.max(0, Math.min(100, (bp ?? 0) / 100))}%`;

/** L'univers Clients : ses onglets, la barre partagée, le Résumé. */
export function StatsClients() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((x) => x.id === params.get('onglet'))?.id ?? 'resume') as TabId;
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const ancre = useAncreDuJour();
  const today = isoOf(ancre);
  const [settings, setSettings] = useState<StatsSettings>(() => defaultSettings(ancre));
  const [view, setView] = useState<ClientsSummaryView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const shown = useRef(false);
  const busy = status.phase === 'loading';
  const a = rangeOf(settings.a);
  const b = settings.b ? rangeOf(settings.b) : null;
  const request = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gln || tab !== 'resume' || !request.from || !request.to) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Le voile tombe tout de suite, l'appel attend la fin de la frappe (même règle que les Ventes).
    setStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.clientsSummary(request)
        .then((v) => { if (!alive) return; shown.current = true; setView(v); setStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Les chiffres précédents sont JETÉS : garder ceux d'une période qu'on vient de quitter mentirait.
          setView(null);
          setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, shown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, request]);

  const pick = (id: TabId) => {
    const next = new URLSearchParams(params);
    if (id === 'resume') next.delete('onglet'); else next.set('onglet', id);
    setParams(next, { replace: true });
  };

  return (
    <>
      <div>
        <h1 style={{ margin: '0 0 10px', fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Clients')}</h1>
        <Tabs size="universe" label={t('Statistiques des clients')} panelId="stats-clients-panel" value={tab} onChange={pick}
          items={TABS.map((x) => ({ id: x.id, label: t(x.label), icon: x.icon }))} />
      </div>
      <div id="stats-clients-panel" role="tabpanel">
        {tab === 'resume' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Le client est-il <1>identifié</1>, revient-il, que vaut-il, et peut-on le joindre.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (view && !busy) exportClientsResume(view, currentLang()); }} disabled={!view || busy}
                title={t('On exporte ce que l’on voit')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: view && !busy ? 'pointer' : 'default', opacity: view && !busy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <StatsBar value={settings} onChange={setSettings} today={today}
              scope={view ? t('{tickets} · {fiches}', { tickets: t('{n} ticket', { n: view.a.tickets }), fiches: t('{n} fiche', { n: view.reach.files }) }) : ''}
              periodNote={asOfNote(view?.asOf ?? null, view?.period, view?.reference)}
              options={view?.filterOptions ?? {}} seasons={view?.seasonPeriods ?? []} />
            {!gln && (
              <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses clients.')} /></div>
            )}
            {gln && status.phase === 'failed' && <ReadFailed message={status.message} />}
            {gln && !view && status.phase === 'loading' && <ClientsPlaceholder />}
            {view && (
              // ⚠️ PENDANT UNE LECTURE, LES CHIFFRES D'AVANT SONT VOILÉS ET DITS : ils ne sont plus ceux des réglages affichés.
              <div aria-busy={busy} style={{ opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
                <ClientsResume view={view} lang={currentLang()} />
              </div>
            )}
          </>
        ) : tab === 'fidelite' ? (
          <LoyaltyTab settings={settings} onSettings={setSettings} gln={gln} today={today} />
        ) : tab === 'campagnes' ? (
          <CampaignsTab gln={gln} />
        ) : (
          <ReportTab gln={gln} />
        )}
      </div>
    </>
  );
}

/** Le Résumé des clients : six indicateurs, l'encartement dans le temps, qui joindre, qui encarte, ce que valent les clients, la rétention. */
function ClientsResume({ view, lang }: { view: ClientsSummaryView; lang: string }) {
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  const { a, r } = view;
  return (
    <>
      <div style={KPI_ROW}>
        <KpiCard label={t('Encartement')} icon="card" tone="teal" value={pc(a.identifiedTicketsBp)} growth={view.growth.identifiedTickets}
          refLabel={tip(pc(r.identifiedTicketsBp))} sub={t('{n} ticket identifié', { n: a.identifiedTickets })}
          series={view.buckets.map((x) => x.identifiedTicketsBp)} />
        <KpiCard label={t('Clients actifs')} icon="users" tone="blue" value={nombreClients(a.activeClients)} growth={view.growth.activeClients}
          refLabel={tip(nombreClients(r.activeClients))} sub={t('{n} nouveau', { n: a.newClients })} series={view.buckets.map((x) => x.activeClients)} />
        <KpiCard label={t('Taux de réachat')} icon="rotate" tone="violet" value={pc(a.repurchaseBp)} growth={view.growth.repurchase}
          refLabel={tip(pc(r.repurchaseBp))} sub={t('achètent au moins deux jours différents')} series={[]} />
        <KpiCard label={t('Panier encarté')} icon="star" tone="amber" value={eur0(a.identifiedBasketCents)} growth={view.growth.identifiedBasket}
          refLabel={tip(eur0(r.identifiedBasketCents))}
          sub={a.basketLiftBp === null ? t('anonymes : {panier}', { panier: eur0(a.anonymousBasketCents) })
            : t('{ecart} face aux anonymes ({panier})', { ecart: dTx(a.basketLiftBp), panier: eur0(a.anonymousBasketCents) })} series={[]} />
        <KpiCard label={t('CA encarté')} icon="trendUp" tone="teal" value={pc(a.identifiedRevenueBp)} growth={view.growth.identifiedRevenue}
          refLabel={tip(pc(r.identifiedRevenueBp))} sub={t('{montant} TTC', { montant: eur0(a.identifiedNetTtcCents) })} series={[]} />
        <KpiCard label={t('Joignables')} icon="send" tone="blue" value={pc(view.reach.shares.any)} growth={null} neutral
          refLabel={t('Pas de comparaison pour cet indicateur')} sub={t('sur {n} fiche client', { n: view.reach.files })} series={[]} />
      </div>
      <div style={CARD_ROW}>
        <Encartement view={view} lang={lang} />
        <Reach view={view} />
      </div>
      <div style={CARD_ROW}>
        <Sellers view={view} />
        <Segments view={view} />
      </div>
      <Cohorts view={view} lang={lang} />
    </>
  );
}

/** « 4 clients » — un compte, accordé. */
const nombreClients = (n: number): string => t('{n} client', { n });

/** L'ENCARTEMENT, PÉRIODE APRÈS PÉRIODE — la part des tickets encartés de chaque seau, et la moyenne de la période. */
function Encartement({ view, lang }: { view: ClientsSummaryView; lang: string }) {
  const { tipProps, tip } = useChartTip();
  const avg = view.a.identifiedTicketsBp;
  const refName = view.reference.kind === 'n1' ? t('N-1') : t('la comparaison');
  const insight = !view.buckets.length ? t('Aucun jour ouvré dans la période choisie.')
    : t('{part} des tickets portent une carte client · {ecart} face à {ref}', { part: pc(avg), ecart: dTx(view.growth.identifiedTickets), ref: refName });
  const step = Math.max(1, Math.ceil(view.buckets.length / 7));
  return (
    <div style={{ ...CARD, flex: '2 1 560px', minWidth: 'min(100%,520px)' }}>
      <CardHead icon="card" tone="teal" title={t('L’encartement, période après période')} insight={insight} />
      {view.buckets.length ? (
        <div style={{ padding: '10px 16px 14px' }}>
          <div style={{ position: 'relative', height: 150, marginLeft: 36 }}>
            {[[10000, '100 %'], [5000, '50 %'], [0, '0 %']].map(([v, l]) => (
              <span key={l} style={{ position: 'absolute', right: 'calc(100% + 8px)', bottom: `${Number(v) / 100}%`, transform: 'translateY(50%)', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{l}</span>
            ))}
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: '50%', borderTop: '1px dashed var(--ink-100)' }} />
            {avg !== null && <div style={{ position: 'absolute', left: 0, right: 0, bottom: width(avg), borderTop: '2px dashed var(--ink-700)', zIndex: 2, pointerEvents: 'none' }} />}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
              {view.buckets.map((x) => (
                <div key={x.key} {...tipProps({
                  label: bucketLabels(x.key, view.grain, lang).tip,
                  value: x.tickets ? t('{part} encartés', { part: pc(x.identifiedTicketsBp) }) : t('aucun ticket'),
                  detail: x.tickets ? t('{n} ticket encarté sur {total}', { n: x.identifiedTickets, total: x.tickets }) : undefined,
                })} style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{
                    width: '100%', height: width(x.identifiedTicketsBp), borderRadius: '5px 5px 2px 2px',
                    background: avg !== null && (x.identifiedTicketsBp ?? 0) >= avg ? 'var(--teal-500)' : 'var(--teal-300)',
                  }} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, margin: '6px 0 0 36px' }}>
            {view.buckets.map((x, i) => (
              <span key={x.key} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'visible' }}>
                {i % step === 0 ? bucketLabels(x.key, view.grain, lang).axis : ''}
              </span>
            ))}
          </div>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
            <span style={{ width: 16, borderTop: '2px dashed var(--ink-700)' }} />
            {t('moyenne {part} · vert vif = au-dessus de la moyenne', { part: pc(avg) })}
          </span>
          {tip}
        </div>
      ) : (
        <div style={EMPTY}>{t('Aucun jour ouvré dans la période choisie.')}</div>
      )}
    </div>
  );
}

/** QUI PEUT-ON CONTACTER — les fiches joignables par canal, sur au moins un, et sans aucun. */
function Reach({ view }: { view: ClientsSummaryView }) {
  const s = view.reach;
  const rows: { label: string; bp: number | null; n: number; color: string }[] = [
    { label: t('SMS'), bp: s.shares.sms, n: s.sms, color: TONES.teal[2] },
    { label: t('Wallet'), bp: s.shares.wallet, n: s.wallet, color: TONES.violet[2] },
    { label: t('E-mail'), bp: s.shares.email, n: s.email, color: TONES.blue[2] },
    { label: t('Au moins un canal'), bp: s.shares.any, n: s.any, color: 'var(--teal-800)' },
    { label: t('Aucun canal'), bp: s.shares.none, n: s.files - s.any, color: TONES.rose[2] },
  ];
  const insight = s.files ? t('{part} des fiches joignables · {sans}', { part: pc(s.shares.any), sans: t('{n} fiche sans aucun canal', { n: s.files - s.any }) })
    : t('Aucune fiche client dans ce magasin.');
  return (
    <div style={{ ...CARD, flex: '1 1 300px', minWidth: 'min(100%,280px)' }}>
      <CardHead icon="send" tone="blue" title={t('Qui peut-on contacter')} insight={insight} />
      <div style={{ padding: '8px 16px 14px', display: 'flex', flexDirection: 'column' }}>
        {rows.map((x) => (
          <div key={x.label} style={{ padding: '9px 0', borderBottom: '1px solid var(--ink-100)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-900)' }}>{x.label}</span>
              <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{pc(x.bp)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
              <span style={{ flex: 1, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: width(x.bp), background: x.color, borderRadius: 'var(--r-pill)' }} />
              </span>
              <span style={{ width: 62, textAlign: 'right', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('{n} fiche', { n: x.n })}</span>
            </div>
          </div>
        ))}
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 10, lineHeight: 1.45 }}>
          {t('Lu sur les fiches clients, avec la règle des campagnes : numéro mobile et accord SMS, e-mail et accord e-mail, carte wallet active et accord wallet. Tout le fichier, sans les filtres.')}
        </span>
      </div>
    </div>
  );
}

/** QUI ENCARTE — l'encartement de chaque vendeur, face à la moyenne du magasin. */
function Sellers({ view }: { view: ClientsSummaryView }) {
  const { tipProps, tip } = useChartTip();
  const avg = view.a.identifiedTicketsBp;
  const first = view.sellers[0];
  const last = view.sellers[view.sellers.length - 1];
  const insight = !first ? t('Aucune vente sur la période.')
    : view.sellers.length > 1 && last
      ? t('{premier} encarte {part} de ses ventes · {dernier} {partDernier}', { premier: first.name, part: pc(first.identifiedTicketsBp), dernier: last.name, partDernier: pc(last.identifiedTicketsBp) })
      : t('{premier} encarte {part} de ses ventes', { premier: first.name, part: pc(first.identifiedTicketsBp) });
  return (
    <div style={{ ...CARD, flex: '1 1 400px', minWidth: 'min(100%,380px)' }}>
      <CardHead icon="user" tone="amber" title={t('Qui encarte')} insight={insight} />
      <div style={{ padding: '8px 16px 14px', display: 'flex', flexDirection: 'column' }}>
        {view.sellers.map((s, i) => {
          const below = avg !== null && s.identifiedTicketsBp !== null && s.identifiedTicketsBp < avg;
          return (
            <div key={s.id} {...tipProps({ label: s.name, value: t('{part} de ventes encartées', { part: pc(s.identifiedTicketsBp) }), detail: t('moyenne {part}', { part: pc(avg) }) })}
              style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 0', borderBottom: '1px solid var(--ink-100)' }}>
              <Avatar name={s.name} tone={toneOf(i)} size={30} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{s.name}</strong>
                  <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{pc(s.identifiedTicketsBp)}</strong>
                </span>
                <span style={{ display: 'block', position: 'relative', height: 8, marginTop: 6, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)' }}>
                  <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: width(s.identifiedTicketsBp), borderRadius: 'var(--r-pill)', background: below ? 'var(--amber)' : 'var(--teal-500)' }} />
                  {avg !== null && <span style={{ position: 'absolute', top: -3, bottom: -3, left: width(avg), borderLeft: '2px dashed var(--ink-700)' }} />}
                </span>
                <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{t('{n} ticket', { n: s.tickets })}</span>
              </span>
            </div>
          );
        })}
        {!view.sellers.length && <div style={EMPTY}>{t('Aucune vente sur la période.')}</div>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
          <span style={{ width: 0, height: 12, borderLeft: '2px dashed var(--ink-700)' }} />
          {t('moyenne du magasin · orange = sous la moyenne')}
        </span>
        {tip}
      </div>
    </div>
  );
}

/** CE QUE VALENT LES CLIENTS — les segments RFM du cœur : leur part des clients et du chiffre. */
function Segments({ view }: { view: ClientsSummaryView }) {
  const seg = view.segments;
  const champions = seg.state === 'ok' ? seg.rows.find((x) => x.segment === 'champions') : undefined;
  const insight = seg.state === 'refused' ? t('Segmentation RFM sur deux ans d’achats.')
    : champions ? t('Les champions : {clients} des clients, {ca} du CA', { clients: pc(champions.clientsBp), ca: pc(champions.revenueBp) })
      : t('Segmentation RFM sur deux ans d’achats.');
  return (
    <div style={{ ...CARD, flex: '1.2 1 460px', minWidth: 'min(100%,420px)' }}>
      <CardHead icon="star" tone="violet" title={t('Ce que valent les clients')} insight={insight} />
      {seg.state === 'refused' ? (
        <div role="note" style={EMPTY}>{seg.reason}</div>
      ) : (
        <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 16, margin: '0 0 6px 150px', fontSize: 'var(--t-micro)', color: 'var(--ink-600)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--ink-400)' }} />{t('part des clients')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--teal-500)' }} />{t('part du CA')}</span>
          </div>
          {seg.rows.map((x) => {
            const tone: Tone = RFM_SEGMENT_TONES[x.segment] ?? 'blue';
            return (
              <div key={x.segment} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ width: 138, flexShrink: 0, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: TONES[tone][2], flexShrink: 0, marginTop: 4 }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-900)' }}>{rfmSegmentLabel(x.segment)}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', lineHeight: 1.35 }}>{rfmSegmentHint(x.segment)}</span>
                  </span>
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Bar bp={x.clientsBp} color="var(--ink-400)" fg="var(--ink-600)" />
                  <Bar bp={x.revenueBp} color="var(--teal-500)" fg="var(--teal-800)" strong />
                  <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                    {t('{clients} · panier {panier}', { clients: nombreClients(x.clients), panier: eur0(x.basketCents) })}
                  </span>
                </span>
              </div>
            );
          })}
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 10, lineHeight: 1.45 }}>
            {t('Segments RFM du cœur — récence, fréquence et montant — sur {n} jours d’achats ({clients}). Tout le fichier, sans les filtres.', {
              n: seg.windowDays, clients: nombreClients(seg.population),
            })}
          </span>
        </div>
      )}
    </div>
  );
}

/** Une barre de part, et sa valeur. */
function Bar({ bp, color, fg, strong = false }: { bp: number | null; color: string; fg: string; strong?: boolean }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ flex: 1, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: width(bp), background: color, borderRadius: 'var(--r-pill)' }} />
      </span>
      <span style={{ width: 44, textAlign: 'right', fontSize: 'var(--t-meta)', fontWeight: strong ? 700 : 400, color: fg, fontVariantNumeric: 'tabular-nums' }}>{pc(bp)}</span>
    </span>
  );
}

/** `AAAAMM` → « avr. 26 » dans la langue du compte. */
const monthLabel = (month: string, lang: string): string => {
  const d = new Date(Date.UTC(+month.slice(0, 4), +month.slice(4, 6) - 1, 15));
  return `${new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(d)} ${month.slice(2, 4)}`;
};

/** REVIENNENT-ILS ? — les six derniers mois de recrutement, et la part revenue acheter 1 à 5 mois plus tard. */
function Cohorts({ view, lang }: { view: ClientsSummaryView; lang: string }) {
  const { tipProps, tip } = useChartTip();
  const insight = view.cohortReturn3Bp === null ? t('Pas encore trois mois de recul sur les clients recrutés.')
    : t('À 3 mois, {part} des nouveaux clients sont revenus', { part: pc(view.cohortReturn3Bp) });
  // `mix` : la part de vert de la case (plus on revient, plus elle est foncée), ou `null` pour un fond uni.
  const cell = (label: string, mix: number | null, base: string, fg: string, tipText?: { label: string; value: string }) => (
    <span {...(tipText ? tipProps(tipText) : {})} style={{
      flex: 1, minWidth: 0, height: 32, borderRadius: 6, background: mix === null ? base : `color-mix(in oklch, var(--teal-500) ${mix}%, var(--paper))`, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 'var(--t-dense)', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    }}>{label}</span>
  );
  return (
    <div style={{ ...CARD, flex: '1 1 100%', minWidth: 'min(100%,520px)', marginBottom: 13 }}>
      <CardHead icon="rotate" tone="teal" title={t('Reviennent-ils ? Rétention des nouveaux clients')} insight={insight} />
      <div style={{ padding: '8px 16px 14px', overflowX: 'auto' }}>
        <div role="table" aria-label={t('Rétention des nouveaux clients')} style={{ minWidth: 560, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div role="row" style={{ display: 'flex', gap: 4, alignItems: 'flex-end', ...MONO_LABEL }}>
            <span role="columnheader" style={{ width: 84, flexShrink: 0 }}>{t('Recrutés en')}</span>
            <span role="columnheader" style={{ width: 60, flexShrink: 0, textAlign: 'right' }}>{t('Clients')}</span>
            <span role="columnheader" style={{ flex: 1, textAlign: 'center' }}>{t('Mois 0')}</span>
            {[1, 2, 3, 4, 5].map((k) => <span key={k} role="columnheader" style={{ flex: 1, textAlign: 'center' }}>{`+${k}`}</span>)}
          </div>
          {view.cohorts.map((c) => (
            <div key={c.month} role="row" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span role="rowheader" style={{ width: 84, flexShrink: 0, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)' }}>{monthLabel(c.month, lang)}</span>
              <span role="cell" style={{ width: 60, flexShrink: 0, textAlign: 'right', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{c.size}</span>
              <span role="cell" style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                {c.size > 0 ? cell(pc(10000), null, 'var(--ink-100)', 'var(--ink-700)') : cell('—', null, 'var(--ink-50)', 'var(--ink-400)')}
              </span>
              {c.returnBp.map((bp, k) => (bp === null
                ? <span key={k} role="cell" style={{ flex: 1, minWidth: 0, height: 32 }} />
                : (
                  <span key={k} role="cell" style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                    {cell(pc(bp), Math.round(12 + (bp / 100) * 1.4), '', bp > 4500 ? 'var(--ink-900)' : 'var(--ink-700)', {
                      label: t('Recrutés en {mois} ({clients})', { mois: monthLabel(c.month, lang), clients: nombreClients(c.size) }),
                      value: t('revenus {n} mois après : {part}', { n: k + 1, part: pc(bp) }),
                    })}
                  </span>
                )))}
            </div>
          ))}
        </div>
        <span style={{ display: 'block', marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
          {t('Chaque ligne suit les clients recrutés un mois donné — leur premier achat — : la part qui revient acheter 1, 2… 5 mois plus tard. Tout le fichier, sans les filtres.')}
        </span>
        {tip}
      </div>
    </div>
  );
}

/** Le gabarit de la première lecture : les six tuiles à leur place, sans un chiffre. */
function ClientsPlaceholder() {
  const tiles: { label: Translatable; icon: IconName; tone: Tone }[] = [
    { label: key('Encartement'), icon: 'card', tone: 'teal' }, { label: key('Clients actifs'), icon: 'users', tone: 'blue' },
    { label: key('Taux de réachat'), icon: 'rotate', tone: 'violet' }, { label: key('Panier encarté'), icon: 'star', tone: 'amber' },
    { label: key('CA encarté'), icon: 'trendUp', tone: 'teal' }, { label: key('Joignables'), icon: 'send', tone: 'blue' },
  ];
  return (
    <div aria-busy="true" style={KPI_ROW}>
      {tiles.map((x) => (
        <div key={x.icon + x.tone} style={{ ...KPI_TILE, paddingBottom: 13 }}>
          <KpiLabel label={t(x.label)} icon={x.icon} tone={x.tone} />
          <div style={{ marginTop: 10, height: 22, width: '60%', borderRadius: 6, background: 'var(--ink-100)' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les indicateurs de la période et de la comparaison, puis qui encarte, les joignables et
 * la rétention. ⚠️ Un taux non mesurable reste VIDE — un 0 dans un tableur devient une moyenne fausse.
 */
function exportClientsResume(view: ClientsSummaryView, lang: string): void {
  const money = (c: number | null) => toInputValue(c);
  const code = currentCurrency().code;
  const { a, r } = view;
  exporterCsv(`resume-clients-${view.period.from}-${view.period.to}`, [t('Indicateur'), t('Période'), t('Comparaison')], [
    [t('Encartement (%)'), csvRate(a.identifiedTicketsBp), csvRate(r.identifiedTicketsBp)],
    [t('Tickets identifiés'), String(a.identifiedTickets), String(r.identifiedTickets)],
    [t('Clients actifs'), String(a.activeClients), String(r.activeClients)],
    [t('Nouveaux clients'), String(a.newClients), String(r.newClients)],
    [t('Taux de réachat (%)'), csvRate(a.repurchaseBp), csvRate(r.repurchaseBp)],
    [t('Panier encarté ({code})', { code }), money(a.identifiedBasketCents), money(r.identifiedBasketCents)],
    [t('Panier anonyme ({code})', { code }), money(a.anonymousBasketCents), money(r.anonymousBasketCents)],
    [t('CA encarté (%)'), csvRate(a.identifiedRevenueBp), csvRate(r.identifiedRevenueBp)],
    [],
    [t('Vendeur'), t('Tickets'), t('Encartement (%)')],
    ...view.sellers.map((s) => [s.name, String(s.tickets), csvRate(s.identifiedTicketsBp)]),
    [],
    [t('Canal'), t('Fiches'), t('Part (%)')],
    [t('SMS'), String(view.reach.sms), csvRate(view.reach.shares.sms)],
    [t('Wallet'), String(view.reach.wallet), csvRate(view.reach.shares.wallet)],
    [t('E-mail'), String(view.reach.email), csvRate(view.reach.shares.email)],
    [t('Au moins un canal'), String(view.reach.any), csvRate(view.reach.shares.any)],
    [],
    [t('Recrutés en'), t('Clients'), '+1 (%)', '+2 (%)', '+3 (%)', '+4 (%)', '+5 (%)'],
    ...view.cohorts.map((c) => [monthLabel(c.month, lang), String(c.size), ...c.returnBp.map(csvRate)]),
  ]);
}

// ─── Fidélité ────────────────────────────────────────────────────────────────────────────────────

/**
 * L'ONGLET FIDÉLITÉ : sa lecture et sa barre (la période et sa comparaison — une écriture de cagnotte ne porte aucun article,
 * la ligne des filtres n'est pas offerte), ses tuiles et ses cartes.
 */
function LoyaltyTab({ settings, onSettings, gln, today }: {
  settings: StatsSettings; onSettings: (next: StatsSettings) => void; gln: string | null; today: string;
}) {
  const [view, setView] = useState<ClientsLoyaltyView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const shown = useRef(false);
  const busy = status.phase === 'loading';
  const a = rangeOf(settings.a);
  const b = settings.b ? rangeOf(settings.b) : null;
  const request = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
  }), [a.from, a.to, b?.from, b?.to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gln || !request.from || !request.to) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    setStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.clientsLoyalty(request)
        .then((v) => { if (!alive) return; shown.current = true; setView(v); setStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Les chiffres précédents sont JETÉS : garder ceux d'une période qu'on vient de quitter mentirait.
          setView(null);
          setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, shown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, request]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Ce que la fidélité <1>distribue</1>, ce qui revient en caisse, ce que le magasin doit encore.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
        <button type="button" onClick={() => { if (view && !busy) exportClientsLoyalty(view, currentLang()); }} disabled={!view || busy}
          title={t('On exporte ce que l’on voit')} className="oz-stats-export"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
            whiteSpace: 'nowrap', cursor: view && !busy ? 'pointer' : 'default', opacity: view && !busy ? 1 : 0.55,
          }}>
          <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
        </button>
      </div>
      <StatsBar value={settings} onChange={onSettings} today={today} scope="" filters={false}
        periodNote={asOfNote(view?.asOf ?? null, view?.period, view?.reference)} options={{}} seasons={view?.seasonPeriods ?? []} />
      {!gln && (
        <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses clients.')} /></div>
      )}
      {gln && status.phase === 'failed' && <ReadFailed message={status.message} />}
      {gln && !view && status.phase === 'loading' && <LoyaltyPlaceholder />}
      {view && (
        <div aria-busy={busy} style={{ opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
          <ClientsLoyalty view={view} lang={currentLang()} />
        </div>
      )}
    </>
  );
}

/** La fidélité : cinq indicateurs, la cagnotte mois par mois, les paliers, les titres. */
function ClientsLoyalty({ view, lang }: { view: ClientsLoyaltyView; lang: string }) {
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  const { a, r, outstanding: o, titles: ti } = view;
  const holders = t('{n} client avec une cagnotte', { n: o.holders });
  return (
    <>
      <div style={KPI_ROW}>
        <KpiCard label={t('Cagnotte distribuée')} icon="coin" tone="teal" value={fullMoney(a.earnedCents)} growth={view.growth.earned}
          refLabel={tip(fullMoney(r.earnedCents))} sub={t('{n} client crédité', { n: a.creditedClients })} series={view.buckets.map((x) => x.earnedCents)} />
        <KpiCard label={t('Cagnotte utilisée')} icon="check" tone="blue" value={fullMoney(a.returnedCents)} growth={view.growth.returned}
          refLabel={tip(fullMoney(r.returnedCents))}
          sub={a.usageBp === null ? t('rien de distribué sur la période') : t('{part} de ce qui a été distribué', { part: pc(a.usageBp) })}
          series={view.buckets.map((x) => x.returnedCents)} />
        <KpiCard label={t('Encours cagnotte')} icon="box" tone="violet" value={fullMoney(o.balanceCents)} growth={null} neutral
          refLabel={t('Pas de comparaison pour cet indicateur')}
          sub={o.negatives ? t('{porteurs} · {negatifs}', { porteurs: holders, negatifs: t('{n} en négatif', { n: o.negatives }) }) : holders} series={[]} />
        <KpiCard label={t('Titres en cours')} icon="ticketTag" tone="amber" value={fullMoney(ti.liveCents)} growth={null} neutral
          refLabel={t('Pas de comparaison pour cet indicateur')} sub={t('{n} titre à utiliser', { n: ti.liveCount })} series={[]} />
        <KpiCard label={t('Titres périmés')} icon="clock" tone="rose" value={fullMoney(ti.expiredCents)} growth={null} neutral
          refLabel={t('Pas de comparaison pour cet indicateur')} sub={t('{n} reliquat non utilisé à temps', { n: ti.expiredCount })} series={[]} />
      </div>
      <div style={CARD_ROW}>
        <LoyaltyMonths view={view} lang={lang} />
        <LoyaltyTiers view={view} />
      </div>
      <LoyaltyTitles view={view} />
    </>
  );
}

/** Une hauteur ou une longueur de barre, en part du plus grand montant — un montant négatif ne dessine rien. */
const barLength = (cents: number, max: number): string => `${Math.max(0, Math.min(100, (cents / max) * 100))}%`;

/** Une entrée de légende : la pastille de sa couleur et son nom. */
function LegendItem({ color, label }: { color: string; label: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />{label}</span>;
}

/**
 * LA CAGNOTTE, MOIS PAR MOIS — distribuée et utilisée, sur les six mois qui finissent avec la période.
 * ⚠️ LA PART OFFERTE SE LIT DANS LA BARRE DE LA DISTRIBUÉE (décision utilisateur : le bonus de bienvenue et le crédit posé à
 *    la main sont des cadeaux du magasin, et ils doivent se voir) : elle en est une PART, jamais une barre de plus.
 */
function LoyaltyMonths({ view, lang }: { view: ClientsLoyaltyView; lang: string }) {
  const { tipProps, tip } = useChartTip();
  const max = Math.max(1, ...view.months.map((m) => Math.max(m.earnedCents, m.returnedCents)));
  const earned = view.months.reduce((s, m) => s + m.earnedCents, 0);
  const insight = earned > 0
    ? t('Sur six mois : {montant} distribués, dont {part} utilisés', { montant: fullMoney(earned), part: pc(view.monthsUsageBp) })
    : t('Aucune cagnotte distribuée sur ces six mois.');
  return (
    <div style={{ ...CARD, flex: '2 1 520px', minWidth: 'min(100%,480px)' }}>
      <CardHead icon="coin" tone="teal" title={t('La cagnotte, mois par mois')} insight={insight} />
      <div style={{ display: 'flex', gap: 10, height: 200, padding: '16px 16px 0' }}>
        {view.months.map((m) => (
          <div key={m.month} {...tipProps({
            label: m.partial ? t('{mois} — à date', { mois: monthLabel(m.month, lang) }) : monthLabel(m.month, lang),
            value: m.offeredCents > 0
              ? t('distribuée {distribuee} (dont offerte {offerte}) · utilisée {utilisee}', {
                distribuee: fullMoney(m.earnedCents), offerte: fullMoney(m.offeredCents), utilisee: fullMoney(m.returnedCents),
              })
              : t('distribuée {distribuee} · utilisée {utilisee}', { distribuee: fullMoney(m.earnedCents), utilisee: fullMoney(m.returnedCents) }),
          })} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 4, borderRadius: 6 }}>
            <span style={{ width: '28%', maxWidth: 22, height: barLength(m.earnedCents, max), display: 'flex', flexDirection: 'column', borderRadius: '4px 4px 0 0', overflow: 'hidden' }}>
              <span data-part="offerte" style={{ flex: `0 0 ${barLength(Math.min(m.offeredCents, m.earnedCents), Math.max(1, m.earnedCents))}`, background: 'var(--teal-300)' }} />
              <span style={{ flex: 1, background: 'var(--teal-500)' }} />
            </span>
            <span style={{ width: '28%', maxWidth: 22, height: barLength(m.returnedCents, max), background: 'var(--blue)', borderRadius: '4px 4px 0 0' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '7px 16px 0', borderTop: '1px solid var(--ink-200)' }}>
        {view.months.map((m) => (
          <span key={m.month} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-700)' }}>
            {monthLabel(m.month, lang)}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', padding: '10px 16px 4px', fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
        <LegendItem color="var(--teal-500)" label={t('distribuée')} />
        <LegendItem color="var(--teal-300)" label={t('dont offerte')} />
        <LegendItem color="var(--blue)" label={t('utilisée')} />
      </div>
      <span style={{ display: 'block', padding: '0 16px 13px', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
        {t('Distribuée : gains sur achat, bonus de bienvenue et crédits posés à la main — ces deux derniers offerts par le magasin. Utilisée : déduite d’une vente ou convertie en bon d’achat.')}
      </span>
      {tip}
    </div>
  );
}

/** LES PALIERS — les fiches de chaque palier du barème du magasin, et leur part du chiffre encarté de la période. */
function LoyaltyTiers({ view }: { view: ClientsLoyaltyView }) {
  const tiers = view.tiers;
  const insight = tiers.state === 'off' ? t('Paliers désactivés dans Réglages → Fidélité.')
    : tiers.withoutTier > 0
      ? t('{sans} · paliers {liste}', { sans: t('{n} fiche sans palier', { n: tiers.withoutTier }), liste: tiers.rows.map((x) => x.label).join(' / ') })
      : t('paliers {liste}', { liste: tiers.rows.map((x) => x.label).join(' / ') });
  return (
    <div style={{ ...CARD, flex: '1 1 300px', minWidth: 'min(100%,280px)' }}>
      <CardHead icon="star" tone="violet" title={t('Les paliers')} insight={insight} />
      {tiers.state === 'off' ? (
        <div style={EMPTY}>{t('Les paliers sont désactivés dans Réglages → Fidélité.')}</div>
      ) : (
        <div style={{ padding: '8px 16px 14px', display: 'flex', flexDirection: 'column' }}>
          {tiers.rows.map((x) => (
            <div key={x.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--ink-100)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: x.color ?? 'var(--ink-300)' }} />{x.label}
                </span>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{nombreClients(x.clients)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{ flex: 1, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: width(x.clientsBp), background: x.color ?? 'var(--ink-300)', borderRadius: 'var(--r-pill)' }} />
                </span>
                <span style={{ width: 44, textAlign: 'right', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{pc(x.clientsBp)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4, fontSize: 'var(--t-micro)', color: 'var(--ink-500)' }}>
                <span>{tierRange(x)}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{t('{part} du CA encarté', { part: pc(x.revenueBp) })}</span>
              </div>
            </div>
          ))}
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 10, lineHeight: 1.45 }}>
            {t('Le palier de chaque fiche aujourd’hui, et le chiffre de la période de ses clients.')}
          </span>
        </div>
      )}
    </div>
  );
}

type TierRow = Extract<ClientsLoyaltyView['tiers'], { state: 'on' }>['rows'][number];

/** La tranche d'un palier et son taux : « de 0 € à 1 500 € · 2,0 % », « de 2 501 € et plus · 5,0 % ». */
function tierRange(x: TierRow): string {
  const span = x.toMinor === null ? t('de {de} et plus', { de: eur0(x.fromMinor) }) : t('de {de} à {a}', { de: eur0(x.fromMinor), a: eur0(x.toMinor) });
  return x.rateBp === null ? span : t('{tranche} · {taux}', { tranche: span, taux: pc(x.rateBp) });
}

/** La part d'un montant dans ce qui a été émis, en largeur CSS. */
const shareWidth = (cents: number, of: number): string => `${of > 0 ? Math.max(0, (cents / of) * 100) : 0}%`;

/** LES TITRES : par type, ce qui a été émis, utilisé, ce qui reste à utiliser et ce qui est périmé — à date. */
function LoyaltyTitles({ view }: { view: ClientsLoyaltyView }) {
  const { tipProps, tip } = useChartTip();
  const ti = view.titles;
  const max = Math.max(1, ...ti.rows.map((x) => x.issuedCents));
  const insight = ti.rows.length
    ? t('Reliquats à utiliser : {encours} · périmés : {perimes}', { encours: fullMoney(ti.liveCents), perimes: fullMoney(ti.expiredCents) })
    : t('Aucun titre en montant au registre.');
  return (
    <div style={{ ...CARD, flex: '1 1 100%', minWidth: 'min(100%,520px)', marginBottom: 13 }}>
      <CardHead icon="ticketTag" tone="amber" title={t('Titres : émis, utilisés, périmés')} insight={insight} />
      <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column' }}>
        {ti.rows.map((x) => {
          const name = typeUi(x.type).l;
          return (
            <div key={x.type} {...tipProps({
              label: name,
              value: t('{montant} émis', { montant: fullMoney(x.issuedCents) }),
              detail: t('utilisé {utilise} · à utiliser {encours} · périmé {perime}', { utilise: fullMoney(x.usedCents), encours: fullMoney(x.liveCents), perime: fullMoney(x.expiredCents) }),
            })} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
              <span style={{ width: 150, flexShrink: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{name}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                  {t('{titres} · {montant} émis', { titres: t('{n} titre', { n: x.count }), montant: fullMoney(x.issuedCents) })}
                </span>
              </span>
              <span style={{ flex: 1, minWidth: 200 }}>
                <span style={{ display: 'block', width: barLength(x.issuedCents, max), minWidth: 40 }}>
                  <span style={{ display: 'flex', height: 14, borderRadius: 'var(--r-pill)', overflow: 'hidden', background: 'var(--ink-100)' }}>
                    <span style={{ width: shareWidth(x.usedCents, x.issuedCents), background: 'var(--teal-500)' }} />
                    <span style={{ width: shareWidth(x.liveCents, x.issuedCents), background: 'var(--blue)' }} />
                    <span style={{ width: shareWidth(x.expiredCents, x.issuedCents), background: 'var(--rose)' }} />
                  </span>
                </span>
              </span>
              <span style={{ display: 'flex', gap: 14, fontSize: 'var(--t-dense)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                <span style={{ color: 'var(--teal-800)', fontWeight: 700 }}>{fullMoney(x.usedCents)}</span>
                <span style={{ color: 'var(--blue-fg)', fontWeight: 700 }}>{fullMoney(x.liveCents)}</span>
                <span style={{ color: 'var(--rose-fg)', fontWeight: 700 }}>{fullMoney(x.expiredCents)}</span>
              </span>
            </div>
          );
        })}
        {!ti.rows.length && <div style={EMPTY}>{t('Aucun titre en montant au registre.')}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', marginTop: 10, fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>
          <LegendItem color="var(--teal-500)" label={t('utilisé')} />
          <LegendItem color="var(--blue)" label={t('à utiliser')} />
          <LegendItem color="var(--rose)" label={t('périmé')} />
          <span style={{ marginLeft: 'auto' }}>{t('Longueur de barre = montant émis')}</span>
        </div>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 8, lineHeight: 1.45 }}>
          {t('Tous les titres en montant du registre, à date — la période ne s’y applique pas. Un titre périmé garde son reliquat : le magasin peut choisir de l’honorer.')}
        </span>
        {tip}
      </div>
    </div>
  );
}

/** Le gabarit de la première lecture de la Fidélité : les cinq tuiles à leur place, sans un chiffre. */
function LoyaltyPlaceholder() {
  const tiles: { label: Translatable; icon: IconName; tone: Tone }[] = [
    { label: key('Cagnotte distribuée'), icon: 'coin', tone: 'teal' }, { label: key('Cagnotte utilisée'), icon: 'check', tone: 'blue' },
    { label: key('Encours cagnotte'), icon: 'box', tone: 'violet' }, { label: key('Titres en cours'), icon: 'ticketTag', tone: 'amber' },
    { label: key('Titres périmés'), icon: 'clock', tone: 'rose' },
  ];
  return (
    <div aria-busy="true" style={KPI_ROW}>
      {tiles.map((x) => (
        <div key={x.icon} style={{ ...KPI_TILE, paddingBottom: 13 }}>
          <KpiLabel label={t(x.label)} icon={x.icon} tone={x.tone} />
          <div style={{ marginTop: 10, height: 22, width: '60%', borderRadius: 6, background: 'var(--ink-100)' }} />
        </div>
      ))}
    </div>
  );
}

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : la cagnotte de la période et de la comparaison, l'encours, les six mois, les paliers et
 * les titres. ⚠️ Un taux non mesurable reste VIDE — un 0 dans un tableur devient une moyenne fausse.
 */
function exportClientsLoyalty(view: ClientsLoyaltyView, lang: string): void {
  const money = (c: number | null) => toInputValue(c);
  const code = currentCurrency().code;
  const { a, r, outstanding: o, titles: ti } = view;
  exporterCsv(`fidelite-clients-${view.period.from}-${view.period.to}`, [t('Indicateur'), t('Période'), t('Comparaison')], [
    [t('Cagnotte distribuée ({code})', { code }), money(a.earnedCents), money(r.earnedCents)],
    [t('Dont offerte ({code})', { code }), money(a.offeredCents), money(r.offeredCents)],
    [t('Cagnotte utilisée ({code})', { code }), money(a.returnedCents), money(r.returnedCents)],
    [t('Part utilisée (%)'), csvRate(a.usageBp), csvRate(r.usageBp)],
    [t('Clients crédités'), String(a.creditedClients), String(r.creditedClients)],
    [],
    [t('À date'), t('Valeur')],
    [t('Encours cagnotte ({code})', { code }), money(o.balanceCents)],
    [t('Clients avec une cagnotte'), String(o.holders)],
    [t('Clients en négatif'), String(o.negatives)],
    [t('Titres en cours ({code})', { code }), money(ti.liveCents)],
    [t('Titres périmés ({code})', { code }), money(ti.expiredCents)],
    [],
    [t('Mois'), t('Distribuée ({code})', { code }), t('Dont offerte ({code})', { code }), t('Utilisée ({code})', { code })],
    ...view.months.map((m) => [monthLabel(m.month, lang), money(m.earnedCents), money(m.offeredCents), money(m.returnedCents)]),
    [],
    [t('Palier'), t('Clients'), t('Part des clients (%)'), t('Part du CA encarté (%)')],
    ...(view.tiers.state === 'on' ? view.tiers.rows.map((x) => [x.label, String(x.clients), csvRate(x.clientsBp), csvRate(x.revenueBp)]) : []),
    [],
    [t('Type de titre'), t('Titres'), t('Émis ({code})', { code }), t('Utilisé ({code})', { code }), t('À utiliser ({code})', { code }), t('Périmé ({code})', { code })],
    ...ti.rows.map((x) => [typeUi(x.type).l, String(x.count), money(x.issuedCents), money(x.usedCents), money(x.liveCents), money(x.expiredCents)]),
  ]);
}

// ─── Campagnes ───────────────────────────────────────────────────────────────────────────────────

/**
 * L'ONGLET CAMPAGNES : toutes les campagnes du magasin (sans période — le dessin n'en a pas), ce qui est parti, ce qui est
 * revenu, et ce que le groupe témoin dit de l'effet. Ni « délivrés », ni « STOP », ni « CA rapporté » (décision S4).
 */
function CampaignsTab({ gln }: { gln: string | null }) {
  const [view, setView] = useState<ClientsCampaignsView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });

  useEffect(() => {
    if (!gln) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    setStatus({ phase: 'loading' });
    api.clientsCampaigns()
      .then((v) => { if (!alive) return; setView(v); setStatus({ phase: 'ready' }); })
      .catch((e: unknown) => {
        if (!alive) return;
        setView(null);
        setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { alive = false; };
  }, [gln]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Toutes les campagnes du magasin : <1>ce qui est parti, ce qui est revenu</1>.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
        <button type="button" onClick={() => { if (view) exportClientsCampaigns(view); }} disabled={!view}
          title={t('On exporte ce que l’on voit')} className="oz-stats-export"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
            whiteSpace: 'nowrap', cursor: view ? 'pointer' : 'default', opacity: view ? 1 : 0.55,
          }}>
          <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
        </button>
      </div>
      {!gln && (
        <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses clients.')} /></div>
      )}
      {gln && status.phase === 'failed' && <ReadFailed message={status.message} />}
      {gln && !view && status.phase === 'loading' && <CampaignsPlaceholder />}
      {view && (view.rows.length ? <ClientsCampaigns view={view} /> : (
        <div className="dcard"><EmptyState icon="megaphone" title={t('Aucune campagne envoyée')} note={t('Les campagnes envoyées depuis Marketing apparaîtront ici, avec ce que leurs destinataires ont fait.')} /></div>
      ))}
    </>
  );
}

/** Le nom d'un canal ; un canal que l'écran ne connaît pas reste lisible sous son code. */
const channelName = (c: string): string => (c === 'email' || c === 'sms' || c === 'wallet_push' ? channelLabel(c) : c);

/** Les campagnes : quatre indicateurs, le tableau, et quel canal fait revenir. */
function ClientsCampaigns({ view }: { view: ClientsCampaignsView }) {
  const x = view.totals;
  const none = t('Pas de comparaison pour cet indicateur');
  return (
    <>
      <div style={KPI_ROW}>
        <KpiCard label={t('Campagnes')} icon="megaphone" tone="blue" value={nombre(x.campaigns)} growth={null} neutral refLabel={none}
          sub={t('{n} en cours', { n: x.inProgress })} series={[]} />
        <KpiCard label={t('Messages envoyés')} icon="send" tone="teal" value={nombre(x.sent)} growth={null} neutral refLabel={none}
          sub={t('tous canaux')} series={[]} />
        <KpiCard label={t('Destinataires revenus')} icon="rotate" tone="amber" value={pc(x.buyersBp)} growth={null} neutral refLabel={none}
          sub={t('{revenus} · fenêtre de {jours}', { revenus: t('{n} destinataire revenu', { n: x.buyers }), jours: t('{n} jour', { n: view.windowDays }) })} series={[]} />
        <KpiCard label={t('Effet prouvé')} icon="target" tone="violet" value={nombre(x.proven)} growth={null} neutral refLabel={none}
          sub={x.measured ? t('sur {n} campagne mesurée face au témoin', { n: x.measured }) : t('aucune campagne avec groupe témoin')} series={[]} />
      </div>
      {view.truncated && (
        <div role="note" style={{ ...EMPTY, padding: '0 0 10px', textAlign: 'left' }}>
          {t('Les {n} campagnes les plus récentes : les plus anciennes ne sont pas comptées.', { n: view.rows.length })}
        </div>
      )}
      <CampaignsTable view={view} />
      <CampaignChannels view={view} />
    </>
  );
}

type CampaignSort = 'name' | 'sent' | 'buyers' | 'effect';
type CampaignRow = ClientsCampaignsView['rows'][number];

/** La valeur de tri d'une ligne ; un effet non mesuré se range après tous les autres. */
const sortValue = (r: CampaignRow, k: CampaignSort): number | string =>
  k === 'name' ? r.name : k === 'sent' ? r.sent : k === 'buyers' ? (r.buyersBp ?? -1) : (r.lift ? r.lift.pointsBp : Number.NEGATIVE_INFINITY);

/** L'effet d'une campagne, en court : l'écart concluant, sinon pourquoi on ne conclut pas. La phrase entière est au survol. */
function effectShort(r: CampaignRow): string {
  if (!r.holdout || !r.lift) return t('sans témoin');
  if (r.lift.conclusive) return t('{v} pts', { v: signedPoints(r.lift.pointsBp) });
  return r.lift.reason === 'too_few' ? t('trop peu de témoins') : t('non concluant');
}

/** LE TABLEAU DES CAMPAGNES — triable par colonne ; la ligne Total est celle du serveur. */
function CampaignsTable({ view }: { view: ClientsCampaignsView }) {
  const [sort, setSort] = useState<{ k: CampaignSort; d: 1 | -1 } | null>(null);
  const rows = sort ? [...view.rows].sort((a, b) => {
    const va = sortValue(a, sort.k);
    const vb = sortValue(b, sort.k);
    return sort.d * (typeof va === 'string' ? va.localeCompare(String(vb)) : va - (vb as number));
  }) : view.rows;
  const cols: { k: CampaignSort | null; label: string; right: boolean }[] = [
    { k: 'name', label: t('Campagne'), right: false }, { k: 'sent', label: t('Envois'), right: true },
    { k: 'buyers', label: t('Acheteurs'), right: true }, { k: null, label: t('Témoin'), right: true }, { k: 'effect', label: t('Effet'), right: true },
  ];
  const grid = 'minmax(220px,2fr) repeat(4,minmax(96px,1fr))';
  const head = (c: (typeof cols)[number]) => {
    const on = sort && c.k === sort.k;
    return (
      <span key={c.label} role="columnheader" aria-sort={on ? (sort!.d < 0 ? 'descending' : 'ascending') : 'none'}
        style={{ display: 'flex', justifyContent: c.right ? 'flex-end' : 'flex-start' }}>
        {c.k ? (
          <button type="button" onClick={() => setSort((cur) => ({ k: c.k!, d: cur && cur.k === c.k ? (cur.d === 1 ? -1 : 1) : (c.k === 'name' ? 1 : -1) }))}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', color: on ? 'var(--teal-800)' : 'var(--ink-600)' }}>
            {c.label}{on ? (sort!.d < 0 ? ' ↓' : ' ↑') : ''}
          </button>
        ) : c.label}
      </span>
    );
  };
  const cell: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' };
  const x = view.totals;
  return (
    <div style={{ ...CARD, marginBottom: 13 }}>
      <div style={{ overflowX: 'auto' }}>
        <div role="table" aria-label={t('Les campagnes du magasin')} style={{ minWidth: 640 }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--ink-200)', ...MONO_LABEL }}>
            {cols.map(head)}
          </div>
          {rows.map((r) => {
            const etat = campaignState(r.status);
            const phrase = phraseEcart({ lift: r.lift, holdout: r.holdout });
            return (
              <div key={r.id} role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center' }}>
                <span role="cell" style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                    {channelName(r.channel)}<Badge tone={etat.tone} size="xs">{etat.label}</Badge>
                  </span>
                </span>
                <span role="cell" style={cell}><strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{nombre(r.sent)}</strong></span>
                <span role="cell" style={cell}>
                  <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{pc(r.buyersBp)}</strong>
                  <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)' }}>{t('{n} acheteur', { n: r.buyers })}</span>
                </span>
                <span role="cell" style={cell}>
                  {r.holdout ? (
                    <>
                      <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)' }}>{pc(r.holdout.buyersBp)}</strong>
                      <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)' }}>{t('{n} témoin', { n: r.holdout.size })}</span>
                    </>
                  ) : <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>—</span>}
                </span>
                <span role="cell" title={phrase ?? undefined} style={cell}>
                  <strong style={{ fontSize: 'var(--t-dense)', color: r.lift?.conclusive ? (r.lift.pointsBp > 0 ? 'var(--teal-800)' : 'var(--rose-fg)') : 'var(--ink-500)' }}>{effectShort(r)}</strong>
                </span>
              </div>
            );
          })}
          <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, padding: '10px 16px', background: 'var(--ink-50)', alignItems: 'center', fontWeight: 700 }}>
            <span role="cell" style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{t('Total · {n} campagne', { n: x.campaigns })}</span>
            <span role="cell" style={cell}>{nombre(x.sent)}</span>
            <span role="cell" style={cell}>{pc(x.buyersBp)}</span>
            <span role="cell" style={cell}>{t('{n} campagne mesurée', { n: x.measured })}</span>
            <span role="cell" style={cell}>{t('{n} effet prouvé', { n: x.proven })}</span>
          </div>
        </div>
      </div>
      <span style={{ display: 'block', padding: '10px 16px 13px', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
        {t('Acheteurs : les destinataires servis qui ont acheté dans la fenêtre de {n} jours suivant le départ. Une partie serait venue sans message : seul l’écart au groupe témoin dit ce que la campagne a changé, et il ne se compte prouvé que s’il est concluant.', { n: view.windowDays })}
      </span>
    </div>
  );
}

/** QUEL CANAL FAIT REVENIR — la part des destinataires revenus par canal, et ses campagnes à l'effet prouvé. */
function CampaignChannels({ view }: { view: ClientsCampaignsView }) {
  const max = Math.max(1, ...view.channels.map((x) => x.buyersBp ?? 0));
  const best = [...view.channels].filter((x) => x.buyersBp !== null).sort((a, b) => (b.buyersBp ?? 0) - (a.buyersBp ?? 0))[0];
  const insight = best ? t('{canal} fait revenir {part} de ses destinataires', { canal: channelName(best.channel), part: pc(best.buyersBp) }) : t('Aucun destinataire servi.');
  return (
    <div style={{ ...CARD, marginBottom: 13 }}>
      <CardHead icon="send" tone="blue" title={t('Quel canal fait revenir')} insight={insight} />
      <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column' }}>
        {view.channels.map((c) => (
          <div key={c.channel} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
            <span style={{ width: 120, flexShrink: 0, fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{channelName(c.channel)}</span>
            <span style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.max(0, ((c.buyersBp ?? 0) / max) * 100)}%`, background: 'var(--teal-500)', borderRadius: 'var(--r-pill)' }} />
                </span>
                <strong style={{ width: 50, textAlign: 'right', fontSize: 'var(--t-dense)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{pc(c.buyersBp)}</strong>
              </span>
              <span style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                {t('{envois} · {campagnes} · effet prouvé : {prouve}', {
                  envois: t('{n} envoi', { n: c.sent }), campagnes: t('{n} campagne', { n: c.campaigns }),
                  prouve: c.measured ? t('{n} sur {mesurees}', { n: c.proven, mesurees: t('{n} campagne mesurée', { n: c.measured }) }) : t('aucune mesure'),
                })}
              </span>
            </span>
          </div>
        ))}
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 10, lineHeight: 1.45 }}>
          {t('Revenir n’est pas l’effet : deux canaux se comparent sur ce qu’ils ont changé, c’est-à-dire face à leur groupe témoin.')}
        </span>
      </div>
    </div>
  );
}

/** Le gabarit de la première lecture des Campagnes : les quatre tuiles à leur place, sans un chiffre. */
function CampaignsPlaceholder() {
  const tiles: { label: Translatable; icon: IconName; tone: Tone }[] = [
    { label: key('Campagnes'), icon: 'megaphone', tone: 'blue' }, { label: key('Messages envoyés'), icon: 'send', tone: 'teal' },
    { label: key('Destinataires revenus'), icon: 'rotate', tone: 'amber' }, { label: key('Effet prouvé'), icon: 'target', tone: 'violet' },
  ];
  return (
    <div aria-busy="true" style={KPI_ROW}>
      {tiles.map((x) => (
        <div key={x.icon} style={{ ...KPI_TILE, paddingBottom: 13 }}>
          <KpiLabel label={t(x.label)} icon={x.icon} tone={x.tone} />
          <div style={{ marginTop: 10, height: 22, width: '60%', borderRadius: 6, background: 'var(--ink-100)' }} />
        </div>
      ))}
    </div>
  );
}

/** « ON EXPORTE CE QUE L'ON VOIT » : une ligne par campagne, puis par canal. ⚠️ Un taux non mesurable reste VIDE. */
function exportClientsCampaigns(view: ClientsCampaignsView): void {
  exporterCsv('campagnes-clients', [t('Campagne'), t('Canal'), t('État'), t('Envois'), t('Servis'), t('Acheteurs'), t('Acheteurs (%)'), t('Témoins'), t('Témoins acheteurs (%)'), t('Effet (points)'), t('Effet concluant')], [
    ...view.rows.map((r) => [
      r.name, channelName(r.channel), campaignState(r.status).label, String(r.sent), String(r.served), String(r.buyers), csvRate(r.buyersBp),
      r.holdout ? String(r.holdout.size) : '', r.holdout ? csvRate(r.holdout.buyersBp) : '', r.lift ? csvRate(r.lift.pointsBp) : '', r.lift ? (r.lift.conclusive ? t('oui') : t('non')) : '',
    ]),
    [],
    [t('Canal'), t('Campagnes'), t('Envois'), t('Servis'), t('Acheteurs'), t('Acheteurs (%)'), t('Mesurées'), t('Effet prouvé')],
    ...view.channels.map((c) => [channelName(c.channel), String(c.campaigns), String(c.sent), String(c.served), String(c.buyers), csvRate(c.buyersBp), String(c.measured), String(c.proven)]),
  ]);
}

// ─── Reporting clients ───────────────────────────────────────────────────────────────────────────

type ReportAxis = ClientsReportView['axis'];
type ReportRow = ClientsReportView['rows'][number];

/** Les axes du sélecteur « Lignes », dans l'ordre du dessin. */
const REPORT_AXES: { id: ReportAxis; label: Translatable }[] = [
  { id: 'segment', label: key('Segment') }, { id: 'city', label: key('Ville') }, { id: 'age', label: key('Âge') },
  { id: 'tier', label: key('Palier') }, { id: 'seniority', label: key('Ancienneté') },
];

/** Les tranches que le serveur rend en CODE, et leur nom. */
const BUCKET_LABELS: Record<string, Translatable> = {
  under25: key('Moins de 25 ans'), '25-34': key('25 à 34 ans'), '35-44': key('35 à 44 ans'), '45-54': key('45 à 54 ans'), '55plus': key('55 ans et plus'),
  this_year: key('Cette année'), '1y': key('Depuis 1 an'), '2-3y': key('2 à 3 ans'), over3y: key('Plus de 3 ans'),
};

/** Le nom d'une ligne : la donnée qu'elle porte (ville, palier), le nom d'un segment ou d'une tranche, ou ce qui manque. */
function reportRowName(axis: ReportAxis, r: ReportRow): string {
  if (axis === 'segment') return r.key ? rfmSegmentLabel(r.key) : t('Sans achat sur deux ans');
  if (axis === 'city') return r.label ?? t('Ville non renseignée');
  if (axis === 'tier') return r.label ?? t('Sans palier');
  const bucket = BUCKET_LABELS[r.key];
  if (bucket) return t(bucket);
  return axis === 'age' ? t('Âge non renseigné') : r.key === 'unknown' ? t('Ancienneté inconnue') : r.key;
}

type ReportColumnKey = 'k' | 'clients' | 'revenue' | 'basket' | 'visits' | 'perClient' | 'loyalty' | 'reachable' | 'last';
const REPORT_CLIENT_COLUMNS: readonly { k: ReportColumnKey; label: Translatable }[] = [
  { k: 'k', label: key('Ligne') }, { k: 'clients', label: key('Clients') }, { k: 'revenue', label: key('CA cumulé') },
  { k: 'basket', label: key('Panier moyen') }, { k: 'visits', label: key('Visites') }, { k: 'perClient', label: key('Visites par client') },
  { k: 'loyalty', label: key('Cagnotte') }, { k: 'reachable', label: key('Joignables') }, { k: 'last', label: key('Dernière visite') },
];
/** La ligne et le nombre de clients restent : sans eux, un tableau de regroupement ne dit plus rien. */
const REPORT_LOCKED: readonly ReportColumnKey[] = ['k', 'clients'];

/** `AAAAMMJJ…` → la date courte dans la langue du compte. */
const fiscalDay = (at: string | null, lang: string): string => (at && at.length >= 8
  ? new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(Date.UTC(+at.slice(0, 4), +at.slice(4, 6) - 1, +at.slice(6, 8)))
  : '—');

/** La valeur de tri d'une ligne ; ce qui ne se mesure pas se range après le reste. */
function reportSortValue(axis: ReportAxis, r: ReportRow, k: ReportColumnKey): string | number {
  switch (k) {
    case 'k': return reportRowName(axis, r);
    case 'last': return r.lastAt ?? '';
    case 'clients': return r.clients;
    case 'revenue': return r.revenueCents;
    case 'basket': return r.basketCents ?? -1;
    case 'visits': return r.visits;
    case 'perClient': return r.visitsPerClient ?? -1;
    case 'loyalty': return r.loyaltyCents;
    case 'reachable': return r.reachableBp ?? -1;
  }
}

/** Une cellule mise en forme. */
function reportCell(axis: ReportAxis, r: ReportRow, k: ReportColumnKey, lang: string): string {
  switch (k) {
    case 'k': return reportRowName(axis, r);
    case 'clients': return nombre(r.clients);
    case 'revenue': return eur0(r.revenueCents);
    case 'basket': return eur0(r.basketCents);
    case 'visits': return nombre(r.visits);
    case 'perClient': return r.visitsPerClient === null ? '—' : nombre(r.visitsPerClient, { fractionDigits: 1 });
    case 'loyalty': return eur0(r.loyaltyCents);
    case 'reachable': return pc(r.reachableBp);
    case 'last': return fiscalDay(r.lastAt, lang);
  }
}

/**
 * L'ONGLET REPORTING CLIENTS : le fichier clients regroupé par l'axe choisi — sans période ni filtre (le dessin n'en a pas),
 * chaque client lu comme sa fiche. Les colonnes se règlent et se mémorisent sur le profil.
 */
function ReportTab({ gln }: { gln: string | null }) {
  const [axis, setAxis] = useState<ReportAxis>('segment');
  const [view, setView] = useState<ClientsReportView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const [sort, setSort] = useState<{ k: ReportColumnKey; d: 1 | -1 }>({ k: 'revenue', d: -1 });
  const lang = currentLang();
  const axisLabel = t(REPORT_AXES.find((x) => x.id === axis)!.label);
  const cols = useStatsColumns('stats-clients-reporting', REPORT_CLIENT_COLUMNS, REPORT_LOCKED,
    (c) => (c.k === 'k' ? axisLabel : t(c.label)), !!gln,
    (message) => showToast(t('Réglage non mémorisé : {v}', { v: message }), 'err'));

  useEffect(() => {
    if (!gln) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    setStatus({ phase: 'loading' });
    api.clientsReport(axis)
      .then((v) => { if (!alive) return; setView(v); setStatus({ phase: 'ready' }); })
      .catch((e: unknown) => {
        if (!alive) return;
        setView(null);
        setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { alive = false; };
  }, [gln, axis]);

  const busy = status.phase === 'loading';
  const shown = view && view.axis === axis ? view : null;
  const rows = shown ? [...shown.rows].sort((a, b) => {
    const va = reportSortValue(axis, a, sort.k);
    const vb = reportSortValue(axis, b, sort.k);
    return sort.d * (typeof va === 'string' ? va.localeCompare(String(vb)) : va - (vb as number));
  }) : [];
  const grid = cols.visible.map((_c, i) => (i === 0 ? 'minmax(170px,1.6fr)' : 'minmax(96px,1fr)')).join(' ');
  const head = (k: ReportColumnKey): string => (k === 'k' ? axisLabel : t(REPORT_CLIENT_COLUMNS.find((c) => c.k === k)!.label));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Le fichier clients <1>regroupé</1> par segment, ville, âge, palier ou ancienneté — sur toute l’histoire de chaque fiche.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
        <button type="button" onClick={() => { if (shown && !busy) exportClientsReport(shown, cols.visible.map((c) => c.k), head, lang); }} disabled={!shown || busy}
          title={t('On exporte ce que l’on voit')} className="oz-stats-export"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
            borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
            whiteSpace: 'nowrap', cursor: shown && !busy ? 'pointer' : 'default', opacity: shown && !busy ? 1 : 0.55,
          }}>
          <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
        </button>
      </div>
      {!gln && (
        <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses clients.')} /></div>
      )}
      {gln && (
        <div style={{ ...CARD, marginBottom: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap', padding: '10px 13px', borderBottom: '1px solid var(--ink-200)' }}>
            <span style={{ width: 58, flexShrink: 0, ...MONO_LABEL }}>{t('Lignes')}</span>
            <span role="group" aria-label={t('Regrouper par')} style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)', flexShrink: 0 }}>
              {REPORT_AXES.map((x) => (
                <button key={x.id} type="button" aria-pressed={x.id === axis} onClick={() => setAxis(x.id)}
                  style={{
                    height: 28, padding: '0 11px', border: 'none', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer',
                    background: x.id === axis ? 'var(--paper)' : 'transparent', color: x.id === axis ? 'var(--ink-900)' : 'var(--ink-600)',
                    boxShadow: x.id === axis ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
                  }}>
                  {t(x.label)}
                </button>
              ))}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 'var(--t-dense)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>
              {shown ? t('{fiches} · {lignes} · chiffres depuis la création de chaque fiche', { fiches: t('{n} fiche', { n: shown.total.clients }), lignes: t('{n} ligne', { n: shown.rows.length }) }) : ''}
            </span>
            <StatsColumnsButton columns={cols.pickerColumns} layout={cols.layout} onChange={cols.change} />
          </div>
          {status.phase === 'failed' && <div style={{ padding: 13 }}><ReadFailed message={status.message} /></div>}
          {!shown && busy && <div aria-busy="true" style={EMPTY}>{t('Lecture du fichier clients…')}</div>}
          {shown?.refused !== undefined && <div role="note" style={EMPTY}>{shown.refused}</div>}
          {shown && shown.refused === undefined && (
            <div aria-busy={busy} style={{ overflowX: 'auto', opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
              <div role="table" aria-label={t('Le fichier clients regroupé')} style={{ minWidth: 'max-content' }}>
                <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                  {cols.visible.map((c, i) => {
                    const on = sort.k === c.k;
                    return (
                      <span key={c.k} role="columnheader" aria-sort={on ? (sort.d < 0 ? 'descending' : 'ascending') : 'none'}
                        style={{ display: 'flex', justifyContent: i === 0 ? 'flex-start' : 'flex-end', padding: '9px 12px' }}>
                        <button type="button" onClick={() => setSort((cur) => ({ k: c.k, d: cur.k === c.k ? (cur.d === 1 ? -1 : 1) : (c.k === 'k' ? 1 : -1) }))}
                          style={{ ...MONO_LABEL, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: on ? 'var(--teal-800)' : 'var(--ink-600)' }}>
                          {head(c.k)}{on ? (sort.d < 0 ? ' ↓' : ' ↑') : ''}
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, background: 'var(--teal-50)', borderBottom: '1.5px solid var(--teal-400)' }}>
                  {cols.visible.map((c, i) => (
                    <span key={c.k} role="cell" style={{ display: 'flex', justifyContent: i === 0 ? 'flex-start' : 'flex-end', padding: '10px 12px', fontSize: 'var(--t-body)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
                      {c.k === 'k' ? t('Total · {n} ligne', { n: rows.length }) : reportCell(axis, shown.total, c.k, lang)}
                    </span>
                  ))}
                </div>
                {rows.map((r) => (
                  <div key={r.key} role="row" style={{ display: 'grid', gridTemplateColumns: grid, borderBottom: '1px solid var(--ink-100)' }}>
                    {cols.visible.map((c, i) => (
                      <span key={c.k} role="cell" style={{
                        display: 'flex', justifyContent: i === 0 ? 'flex-start' : 'flex-end', padding: '9px 12px', fontSize: 'var(--t-body)',
                        fontWeight: i === 0 ? 600 : 500, color: i === 0 ? 'var(--ink-900)' : 'var(--ink-700)', fontVariantNumeric: 'tabular-nums',
                      }}>
                        {reportCell(axis, r, c.k, lang)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** « ON EXPORTE CE QUE L'ON VOIT » : les colonnes affichées, la ligne Total puis les lignes. ⚠️ Un taux non mesurable reste VIDE. */
function exportClientsReport(view: ClientsReportView, keys: readonly ReportColumnKey[], head: (k: ReportColumnKey) => string, lang: string): void {
  const code = currentCurrency().code;
  const money = (c: number | null) => toInputValue(c);
  const cell = (r: ReportRow, k: ReportColumnKey): string => {
    switch (k) {
      case 'k': return reportRowName(view.axis, r);
      case 'clients': return String(r.clients);
      case 'revenue': return money(r.revenueCents);
      case 'basket': return money(r.basketCents);
      case 'visits': return String(r.visits);
      case 'perClient': return r.visitsPerClient === null ? '' : String(r.visitsPerClient);
      case 'loyalty': return money(r.loyaltyCents);
      case 'reachable': return csvRate(r.reachableBp);
      case 'last': return r.lastAt ? fiscalDay(r.lastAt, lang) : '';
    }
  };
  const money3 = new Set<ReportColumnKey>(['revenue', 'basket', 'loyalty']);
  exporterCsv(`reporting-clients-${view.axis}-${view.asOf}`,
    keys.map((k) => (money3.has(k) ? t('{colonne} ({code})', { colonne: head(k), code }) : k === 'reachable' ? t('{colonne} (%)', { colonne: head(k) }) : head(k))),
    [keys.map((k) => (k === 'k' ? t('Total') : cell(view.total, k))), ...view.rows.map((r) => keys.map((k) => cell(r, k)))]);
}
